import { useState } from "react";
import api from "../lib/api";
import { Wand2, Loader2, AlertTriangle, CheckCircle2, Settings2, RefreshCw } from "lucide-react";

const FORMATS = [
  { key: "4:5", label: "4:5 (feed)" },
  { key: "1:1", label: "1:1 (carré)" },
  { key: "16:9", label: "16:9 (large)" },
];

const mediaUrl = (url) =>
  api.defaults.baseURL.replace(/\/api$/, "") + url + "?token=" + (localStorage.getItem("apollo_token") || "");

export default function CreativeStudio() {
  const [post, setPost] = useState("");
  const [format, setFormat] = useState("4:5");
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [corrections, setCorrections] = useState(0); // 1 correction max autorisée => 2 générations max

  const pollJob = async (jobId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/visuals/job/${jobId}`);
      if (data.status === "done") return data;
      if (data.status === "error") throw new Error(data.error || "Génération échouée.");
    }
    throw new Error("Délai de génération dépassé.");
  };

  const generate = async (isCorrection = false) => {
    setError("");
    if (!post.trim()) { setError("Colle d'abord le texte du post."); return; }
    if (!isCorrection) setResult(null);
    setBusy(true);
    try {
      const { data } = await api.post("/visuals/auto", { post_content: post, format });
      const done = await pollJob(data.job_id);
      setResult(done);
      if (isCorrection) setCorrections((n) => n + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Échec de la génération.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="creative-studio">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">
          <Wand2 className="w-8 h-8 text-orange-500" /> Studio créatif
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Colle ton post. Le studio choisit seul la meilleure idée visuelle et produit ton visuel final.
        </p>
      </div>

      <textarea
        data-testid="studio-post-input"
        value={post}
        onChange={(e) => setPost(e.target.value)}
        placeholder="Colle ici le texte de ton post LinkedIn…"
        rows={8}
        className="w-full rounded-xl border border-border bg-background p-4 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40"
      />

      <div className="flex items-center gap-3">
        <button
          data-testid="studio-generate-btn"
          onClick={() => generate(false)}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {busy ? "Création du visuel…" : "Générer le visuel"}
        </button>
        <button
          data-testid="studio-advanced-toggle"
          onClick={() => setAdvanced((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
        >
          <Settings2 className="w-3.5 h-3.5" /> Réglages
        </button>
      </div>

      {advanced && (
        <div className="flex items-center gap-2" data-testid="studio-format-select">
          <span className="text-xs text-muted-foreground mr-1">Format :</span>
          {FORMATS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFormat(f.key)}
              data-testid={`studio-format-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                format === f.key ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div data-testid="studio-error" className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 rounded-lg px-4 py-3">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {busy && !result && (
        <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="studio-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Analyse du post, sélection de la meilleure idée, génération du visuel…
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="studio-result">
          <img
            data-testid="studio-result-image"
            src={mediaUrl(result.url)}
            alt="visuel final"
            className="rounded-2xl border border-border w-full"
          />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {result.concept?.archetype && (
              <span className="px-2.5 py-1 rounded-full bg-muted text-foreground/80">{result.concept.archetype}</span>
            )}
            <span className={`px-2.5 py-1 rounded-full ${result.gilles ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
              {result.gilles ? "Avec Gilles" : "Sans Gilles"}
            </span>
            {result.suggested_text ? (
              <span className="text-muted-foreground">Micro-texte suggéré : <span className="font-semibold">“{result.suggested_text}”</span></span>
            ) : (
              <span className="text-muted-foreground italic">Aucun texte</span>
            )}
            {result.conforme_ocr ? (
              <span className="inline-flex items-center gap-1 text-emerald-500 ml-auto"><CheckCircle2 className="w-3.5 h-3.5" /> Conforme</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-500 ml-auto"><AlertTriangle className="w-3.5 h-3.5" /> Texte parasite détecté</span>
            )}
          </div>
          {corrections < 1 && (
            <button
              data-testid="studio-correct-btn"
              onClick={() => generate(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 hover:border-orange-500 hover:text-orange-500 px-4 py-2 text-sm transition-colors disabled:opacity-60"
            >
              <RefreshCw className="w-4 h-4" /> Demander une correction (1 max)
            </button>
          )}
          {corrections >= 1 && (
            <p className="text-xs text-muted-foreground">Correction déjà utilisée (2 générations max par post).</p>
          )}
        </div>
      )}
    </div>
  );
}
